import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { UserStore, UserDto } from 'stores/users/users-store';
import { useCallback, useEffect, useState } from 'react';
import TailwindMultiSelect from 'widgets/editors/tailwind-multiselect';
import { nameof } from 'utils/object';
import { StudentDto } from 'stores/students/student-model';

interface TailwindMultipleEmployeeEditorProps {
  value: StudentDto[];
  role: string;
  locationIds: string[];
  clearable?: boolean;
  onChange?: (value: UserDto[]) => void;
  users?: UserStore;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

const TailwindMultipleEmployeeEditor: React.FC<TailwindMultipleEmployeeEditorProps> = ({
  value,
  role,
  clearable = true,
  onChange,
  users,
  locationIds,
  placeholder,
  className,
  disabled = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<UserDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const buildODataFilter = useCallback(() => {
    const oDataFilters = [];
    oDataFilters.push({ id: nameof<UserDto>('enabled'), filter: { enabled: true } });
    if (value && (value || []).length > 0)
      oDataFilters.push({
        id: nameof<UserDto>('id'),
        filter: `not(${nameof<UserDto>('id')} in (${(value || []).map(x => x.id).join(',')}))`
      });
    return oDataFilters;
  }, [value]);

  const getUsersMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      const employees = await users.getUsersByRoleAndLocationsAndOData(
        {
          searchQuery: '',
          orderBy: [],
          filter: buildODataFilter().map(x => x.filter),
          skip: 0,
          take: 10
        },
        locationIds,
        role
      );

      setOptions(employees.items || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [users, locationIds, role, buildODataFilter]);

  useEffect(() => {
    if (isInitialLoad) {
      getUsersMethod();
      setIsInitialLoad(false);
    }
  }, [getUsersMethod, isInitialLoad]);

  return (
    <TailwindMultiSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(employee => ({
              id: employee.id,
              text: employee.firstName + ' ' + employee.lastName
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={(value || [])?.map(employee => ({
        id: employee.id,
        text: employee.firstName + ' ' + employee.lastName
      }))}
      onChange={newValue => {
        const selectedEmployees = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
        onChange(selectedEmployees);
      }}
      placeholder={placeholder || t('Select employees')}
      clearable={clearable}
      disabled={disabled}
      className={className}
      onBlur={onBlur}
      onFocus={onFocus}
      searchable={!isLoading}
    />
  );
};

// Wire up the React component to the Redux store
export default connect(['users', UserStore])(TailwindMultipleEmployeeEditor);
